import React from 'react';

const InlineStyles = () => {
  const styles = `
    :root {
      --bg: #f7fafc;
      --surface: #ffffff;
      --text: #1f2937;
      --muted: #6b7280;
      --border: #e5e7eb;
      --primary: #2563eb; /* blue-600 */
      --primary-hover: #1d4ed8; /* blue-700 */
      --success: #16a34a; /* green-600 */
      --danger: #dc2626; /* red-600 */
      --shadow: 0 10px 20px rgba(0,0,0,0.06), 0 2px 6px rgba(0,0,0,0.04);
      --radius: 14px;
    }

    * { box-sizing: border-box; }

    body {
      margin: 0;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
        Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
      background: var(--bg);
      color: var(--text);
    }

    /* RTL support for Urdu */
    [dir="rtl"] { direction: rtl; text-align: right; }
    [dir="rtl"] .erc-decoder { text-align: center; }
    [dir="rtl"] .explanation { text-align: right !important; }

    /* Language-specific font families */
    .app-container { 
      display: flex; 
      justify-content: center; 
      align-items: flex-start; 
      padding: 48px 16px; 
      min-height: 100vh;
      background: var(--bg);
    }

    .app-container[data-lang="mn"] { font-family: "Mongolian Baiti", "Microsoft Sans Serif", Arial, sans-serif; }
    .app-container[data-lang="ur"] { font-family: "Noto Nastaliq Urdu", "Jameel Noori Nastaleeq", "Urdu Typesetting", Arial, sans-serif; direction: rtl; }
    .app-container[data-lang="ru"] { font-family: "Times New Roman", "DejaVu Serif", Georgia, serif; }

    .erc-decoder {
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: var(--radius);
      box-shadow: var(--shadow);
      width: 100%;
      max-width: 960px;
      padding: 32px;
      text-align: center;
      margin-bottom: 48px;
    }

    .erc-decoder h1 { 
      margin: 0 0 8px; 
      font-weight: 700; 
      letter-spacing: -0.01em; 
    }

    .erc-decoder h2 { margin: 0 0 12px; font-size: 22px; }

    .explanation { 
      margin: 16px auto 24px; 
      max-width: 640px; 
      color: var(--muted); 
      line-height: 1.7; 
    }
    .explanation h4 { margin: 0; color: var(--muted); font-weight: 500; }

    .navigationButtons { display: flex; justify-content: center; gap: 16px; width: 100%; margin: 28px 0; }
    @media (max-width: 550px) { .navigationButtons { flex-direction: column; align-items: center; gap: 12px; } }

    .navigationButtons button {
      width: 220px;
      background: transparent;
      color: var(--primary);
      border-radius: 8px;
      border: 1px solid var(--border);
      padding: 10px 14px;
      text-decoration: none;
      min-height: 44px;
      font-size: 14px;
      line-height: 1.2;
      transition: border-color .2s, background .2s;
    }
    .navigationButtons button:hover { border-color: var(--primary); background: #eff6ff; }

    .erc-form { display: flex; flex-direction: row; align-items: stretch; gap: 24px; justify-content: center; }
    @media (max-width: 768px) { .erc-form { flex-direction: column-reverse; } }

    .erc-decoder form {
      display: flex; flex-direction: column; align-items: center;
      background: var(--surface);
      border: 1px solid var(--border);
      border-radius: 12px;
      max-width: 360px; width: 100%;
      margin: 10px auto; padding: 20px;
    }

    .erc-decoder label { margin-bottom: 12px; font-weight: 600; color: var(--text); width: 100%; text-align: left; }
    .erc-decoder label input { margin-top: 6px; width: 100%; }

    .erc-decoder input[type="text"] {
      padding: 12px 14px;
      font-size: 16px;
      border: 1px solid var(--border);
      border-radius: 10px;
      outline: none;
      text-align: center;
      font-family: monospace;
      transition: box-shadow .2s, border-color .2s;
      background: var(--surface);
    }
    .erc-decoder input[type="text"]:focus { border-color: var(--primary); box-shadow: 0 0 0 4px rgba(37,99,235,0.1); }

    .erc-decoder button {
      padding: 12px 18px;
      font-size: 16px;
      background: var(--primary);
      color: #ffffff;
      border: none;
      border-radius: 10px;
      cursor: pointer;
      transition: background-color 0.2s ease, transform 0.02s ease;
      min-height: 44px;
    }
    .erc-decoder button:hover { background-color: var(--primary-hover); }
    .erc-decoder button:active { transform: translateY(1px); }
    .erc-decoder button:focus-visible, .buy-credits-btn:focus-visible, .navigationButtons button:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; box-shadow: 0 0 0 4px rgba(37,99,235,0.2); }

    .erc-decoder .error-message { color: var(--danger); margin-top: 10px; }
    .erc-decoder .decoded-result { margin-top: 20px; }
    .decoded-result p { padding: 10px 12px; background-color: #ecfdf5; color: #065f46; border: 1px solid #a7f3d0; border-radius: 8px; width: 260px; margin: 0 auto; font-weight: 600; font-family: monospace; }
    .erc-decoder .generate-button { margin-top: 16px; background: transparent; color: var(--primary); border: 1px solid var(--border); }
    .erc-decoder .generate-button:hover { border-color: var(--primary); background: #eff6ff; }

    /* Credits banner styles (moved from inline) */
    .credits-display { background: #f8fafc; border: 1px solid var(--border); border-radius: 12px; padding: 14px; margin: 20px auto; max-width: 460px; text-align: center; }
    .free-unlocks { color: var(--success); font-weight: 700; font-size: 18px; }
    .paid-credits { color: var(--primary); font-weight: 700; font-size: 18px; }
    .no-credits { color: var(--danger); font-weight: 700; }
    .buy-credits-btn { margin-top: 10px; padding: 10px 16px; background: var(--primary); color: #fff; border: none; border-radius: 10px; cursor: pointer; font-size: 14px; transition: background .2s; }
    .buy-credits-btn:hover { background: var(--primary-hover); }

    .images { display: flex; justify-content: center; gap: 24px; margin: 40px 0; flex-wrap: wrap; }
    .images div { margin: 0; }
    .images div img { width: 320px; max-width: 40vw; height: auto; border-radius: 12px; border: 1px solid var(--border); box-shadow: var(--shadow); background: #fff; }
    @media (max-width: 420px) { .images div img { max-width: 80vw; } }

    /* Generic content card for static pages */
    .content-card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow); width: 100%; max-width: 960px; padding: 32px; margin: 0 auto 48px; }
    .content-card h1 { margin: 0 0 12px; font-weight: 800; letter-spacing: -0.01em; color: #111827; text-align: center; }
    .content-card .subtitle { text-align: center; color: var(--muted); margin-bottom: 12px; }

    /* Prose styles for readable long-form text */
    .prose { color: var(--text); line-height: 1.75; }
    .prose h2 { margin-top: 28px; margin-bottom: 10px; font-size: 20px; color: #111827; }
    .prose h3 { margin-top: 18px; margin-bottom: 8px; font-size: 16px; color: #111827; }
    .prose p { margin: 0 0 12px; }
    .prose ul { margin: 0 0 12px 20px; padding: 0; }
    .prose li { margin-bottom: 8px; }
    .prose hr { margin: 28px 0; border: none; border-top: 1px solid var(--border); }
    .prose a { color: var(--primary); text-decoration: underline; }
    .prose strong { font-weight: 700; }

    /* Buttons */
    .btn { display: inline-flex; align-items: center; justify-content: center; gap: 8px; padding: 10px 16px; border-radius: 10px; font-weight: 600; cursor: pointer; border: 1px solid var(--border); background: #fff; color: var(--text); transition: background .2s, border-color .2s, color .2s; }
    .btn:hover { background: #f9fafb; }
    .btn:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }
    .btn-primary { background: var(--primary); color: #fff; border-color: var(--primary); }
    .btn-primary:hover { background: var(--primary-hover); border-color: var(--primary-hover); }
    .btn-outline { background: transparent; color: var(--primary); border-color: var(--border); }
    .btn-outline:hover { background: #eff6ff; border-color: var(--primary); }

    /* Model list */
    .model-list { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 16px; margin-top: 16px; }
    .model-list .section { background: var(--surface); border: 1px solid var(--border); border-radius: 12px; padding: 16px; box-shadow: var(--shadow); }
    .model-list .section h2 { margin: 0 0 8px; font-size: 18px; }
    .model-list .section ul { list-style: none; padding: 0; margin: 0; columns: 1; }
    .model-list .section li { padding: 4px 0; border-bottom: 1px dashed #edf2f7; }
    .model-list .section li:last-child { border-bottom: none; }

    /* General text spacing */
    .erc-decoder p { line-height: 1.7; margin-bottom: 12px; }
    .erc-decoder div[style*="marginTop"] p { margin-bottom: 12px; padding-left: 4px; }
    [data-lang="ur"] .erc-decoder div[style*="marginTop"] p { padding-right: 4px; padding-left: 0; }

    /* Footer */
    .footer { margin-top: 32px; padding-top: 16px; border-top: 1px solid var(--border); color: var(--muted); }
    .footer-link { color: var(--muted); text-decoration: none; }
    .footer-link:hover { color: var(--primary); text-decoration: underline; }
    .footer-link:focus-visible { outline: 2px solid var(--primary); outline-offset: 2px; }

    /* Misc */
    #u_0_4_aM { color: inherit; }
  `;

  return (
    <style>
      {styles}
    </style>
  );
};

export default InlineStyles;